import { useState, useEffect } from 'react';
import { X, EnvelopeOpen, CursorClick, PaperPlaneTilt, ArrowsClockwise, MagnifyingGlass, Bell, CircleNotch } from '@phosphor-icons/react';
import { Badge } from '../ui/badge';
import { API } from './constants';

function StatBlock({ label, value, sub, color = 'text-zinc-700' }) {
  return (
    <div className="bg-white border border-zinc-200 p-3 rounded-sm">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-data text-2xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-400">{sub}</div>}
    </div>
  );
}

export function BroadcastDetailPanel({ campaignId, onClose, onCreateFollowUp }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [creatingFollowUp, setCreatingFollowUp] = useState(false);
  const [followUpError, setFollowUpError] = useState('');

  const refresh = () => {
    fetch(`${API}/email/campaigns/${campaignId}/broadcast/stats`, { credentials: 'include' })
      .then(r => r.json()).then(setData).catch(() => {});
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  if (!data) return null;

  const t = data.totals || {};
  let recipients = data.recipients || [];
  if (filter === 'opened') recipients = recipients.filter(r => r.opened_at);
  else if (filter === 'clicked') recipients = recipients.filter(r => r.clicked_at);
  else if (filter === 'no_open') recipients = recipients.filter(r => !r.opened_at && r.sent_at);
  if (search) {
    const q = search.toLowerCase();
    recipients = recipients.filter(r =>
      (r.to_email || '').toLowerCase().includes(q) ||
      (r.contact_name || '').toLowerCase().includes(q) ||
      (r.organization || '').toLowerCase().includes(q)
    );
  }

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '-';

  const handleCreateFollowUp = async (audience) => {
    const nonOpens = (data.totals?.sent || 0) - (data.totals?.unique_opens || 0);
    const expected = audience === 'non_openers' ? nonOpens : audience === 'non_clickers' ? (data.totals?.sent || 0) - (data.totals?.unique_clicks || 0) : (data.totals?.sent || 0);
    if (expected <= 0) {
      setFollowUpError('Geen contacten om naartoe te sturen.');
      return;
    }
    if (!window.confirm(`Maak een nieuwe broadcast aan voor ${expected} ontvanger(s)?\nJe kunt daarna de tekst nog aanpassen voor je verzendt.`)) return;
    setCreatingFollowUp(true);
    setFollowUpError('');
    try {
      const res = await fetch(`${API}/email/campaigns/${campaignId}/broadcast/follow-up`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audience }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Kon follow-up niet aanmaken');
      onCreateFollowUp && onCreateFollowUp(result.campaign);
      onClose();
    } catch (e) {
      setFollowUpError(e.message);
    }
    setCreatingFollowUp(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="broadcast-detail-panel">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Badge className="rounded-sm px-2 py-0.5 text-[10px] bg-purple-100 text-purple-700 border border-purple-200 shrink-0">Broadcast</Badge>
            <h3 className="font-heading text-base font-semibold truncate">{data.name}</h3>
            <span className="text-xs text-zinc-400">· {fmt(data.broadcast_sent_at)}</span>
          </div>
          <button onClick={refresh} className="p-1.5 hover:bg-zinc-100 rounded-sm" title="Vernieuwen" data-testid="broadcast-stats-refresh"><ArrowsClockwise size={16} /></button>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-sm ml-1" data-testid="broadcast-stats-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {data.subject && <div className="text-sm text-zinc-700"><span className="text-zinc-400 mr-2">Onderwerp:</span><strong>{data.subject}</strong></div>}

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="broadcast-stats-grid">
            <StatBlock label="Verzonden" value={t.sent ?? 0} color="text-zinc-800" />
            <StatBlock label="Unieke opens" value={t.unique_opens ?? 0} color="text-emerald-600" />
            <StatBlock label="Open rate" value={`${t.open_rate ?? 0}%`} color="text-emerald-600" />
            <StatBlock label="Unieke clicks" value={t.unique_clicks ?? 0} color="text-[#0055FF]" />
            <StatBlock label="Click rate" value={`${t.click_rate ?? 0}%`} color="text-[#0055FF]" />
          </div>

          {/* Follow-up actions */}
          {(t.sent ?? 0) > 0 && (
            <div className="flex items-center gap-2 flex-wrap p-3 border border-amber-200 bg-amber-50/50 rounded-sm" data-testid="follow-up-actions">
              <Bell size={14} className="text-amber-600 shrink-0" />
              <span className="text-xs font-medium text-amber-800">Follow-up acties:</span>
              <button onClick={() => handleCreateFollowUp('non_openers')} disabled={creatingFollowUp || (t.sent - t.unique_opens) <= 0}
                data-testid="follow-up-non-openers"
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors">
                {creatingFollowUp ? <CircleNotch size={11} className="animate-spin" /> : <Bell size={11} />}
                Reminder naar niet-openers ({Math.max(0, (t.sent || 0) - (t.unique_opens || 0))})
              </button>
              <button onClick={() => handleCreateFollowUp('non_clickers')} disabled={creatingFollowUp || (t.sent - t.unique_clicks) <= 0}
                data-testid="follow-up-non-clickers"
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-amber-300 text-amber-800 bg-white hover:bg-amber-100 disabled:opacity-50 transition-colors">
                Reminder naar niet-klikkers ({Math.max(0, (t.sent || 0) - (t.unique_clicks || 0))})
              </button>
              {followUpError && <span className="text-xs text-red-600 ml-1">{followUpError}</span>}
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: 'all', label: 'Alle ontvangers' },
                { v: 'opened', label: 'Geopend' },
                { v: 'clicked', label: 'Geklikt' },
                { v: 'no_open', label: 'Niet geopend' },
              ].map(f => (
                <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`broadcast-filter-${f.v}`}
                  className={`px-2.5 py-1 text-xs border transition-colors ${filter === f.v ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Zoek op naam of email"
                className="w-full pl-7 pr-3 py-1.5 border border-zinc-200 text-xs rounded-sm focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF]"
                data-testid="broadcast-search"
              />
            </div>
          </div>

          {/* Recipients table */}
          <div className="border border-zinc-200 overflow-x-auto" data-testid="broadcast-recipients-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Ontvanger</th>
                  <th className="text-left px-3 py-2 font-medium">Verzonden</th>
                  <th className="text-left px-3 py-2 font-medium">Geopend</th>
                  <th className="text-left px-3 py-2 font-medium">Geklikt</th>
                </tr>
              </thead>
              <tbody>
                {recipients.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-zinc-400 text-xs">Geen ontvangers in deze selectie</td></tr>
                ) : recipients.map(r => (
                  <tr key={r.tracking_id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`recipient-row-${r.tracking_id}`}>
                    <td className="px-3 py-2 max-w-xs">
                      <div className="text-sm truncate">{r.contact_name || r.to_email}</div>
                      <div className="text-[11px] text-zinc-400 truncate">{r.to_email}{r.organization ? ` · ${r.organization}` : ''}{r.country_code ? ` · ${r.country_code}` : ''}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-600">{fmt(r.sent_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.opened_at ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <EnvelopeOpen size={12} /> {fmt(r.opened_at)}{r.open_count > 1 && <span className="text-[10px] text-emerald-500">×{r.open_count}</span>}
                        </span>
                      ) : <span className="text-zinc-300">-</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.clicked_at ? (
                        <span className="inline-flex items-center gap-1 text-[#0055FF]">
                          <CursorClick size={12} /> {fmt(r.clicked_at)}{r.click_count > 1 && <span className="text-[10px] text-blue-400">×{r.click_count}</span>}
                        </span>
                      ) : <span className="text-zinc-300">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BroadcastsList({ onOpen }) {
  const [items, setItems] = useState(null);

  const refresh = () => {
    fetch(`${API}/email/broadcasts`, { credentials: 'include' })
      .then(r => r.json()).then(d => setItems(d.broadcasts || [])).catch(() => setItems([]));
  };

  useEffect(() => { refresh(); }, []);

  if (items === null) return <div className="text-sm text-zinc-500 p-4">Laden...</div>;

  return (
    <div className="border border-zinc-200 bg-white" data-testid="broadcasts-list">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <PaperPlaneTilt size={16} className="text-purple-600" />
          <h3 className="font-heading text-sm font-semibold">Verzonden Broadcasts</h3>
          <span className="text-xs text-zinc-400">({items.length})</span>
        </div>
        <button onClick={refresh} className="p-1.5 hover:bg-zinc-100 rounded-sm" title="Vernieuwen" data-testid="broadcasts-list-refresh"><ArrowsClockwise size={14} /></button>
      </div>

      {items.length === 0 ? (
        <div className="px-4 py-8 text-center text-zinc-400 text-xs">Nog geen broadcasts verzonden.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Campagne</th>
              <th className="text-left px-4 py-2 font-medium">Verzonden</th>
              <th className="text-center px-4 py-2 font-medium">Ontvangers</th>
              <th className="text-center px-4 py-2 font-medium">Opens</th>
              <th className="text-center px-4 py-2 font-medium">Open rate</th>
              <th className="text-center px-4 py-2 font-medium">Clicks</th>
              <th className="text-center px-4 py-2 font-medium">CTR</th>
            </tr>
          </thead>
          <tbody>
            {items.map(b => (
              <tr key={b.campaign_id}
                onClick={() => onOpen(b.campaign_id)}
                className="border-t border-zinc-100 hover:bg-purple-50/30 cursor-pointer"
                data-testid={`broadcast-row-${b.campaign_id}`}>
                <td className="px-4 py-2.5">
                  <div className="text-sm font-medium">{b.name}</div>
                  <div className="text-[11px] text-zinc-400 truncate max-w-md">{b.subject || '-'}</div>
                </td>
                <td className="px-4 py-2.5 text-xs text-zinc-500">
                  {b.broadcast_sent_at ? new Date(b.broadcast_sent_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                </td>
                <td className="px-4 py-2.5 text-center font-data">{b.sent}</td>
                <td className="px-4 py-2.5 text-center font-data text-emerald-600">{b.unique_opens}</td>
                <td className="px-4 py-2.5 text-center font-data text-emerald-600">{b.open_rate}%</td>
                <td className="px-4 py-2.5 text-center font-data text-[#0055FF]">{b.unique_clicks}</td>
                <td className="px-4 py-2.5 text-center font-data text-[#0055FF]">{b.click_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
